import { AnimatePresence, motion } from "framer-motion";
import { EyeOff, LogOut, Radio, Wifi, WifiOff } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ACTIONS, ROLES } from "../gameData";
import { apiUrl } from "../gameSocket";
import type { GameState, Player } from "../types";

const DISPLAY_SESSION_KEY = "veiled-court-display-session";
const SITE_KEY = "veiled-court-site-password";
/** Frozen so the demo scenes render identically on every load. */
const DEMO_AT = 0;
const DEMO_SCENES = makeDemoScenes();

interface DisplaySession {
  code: string;
  displayToken: string;
}

type DisplayStatus = "connecting" | "live" | "offline";

export function TVApp() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const isDemo = params.get("demo") === "1";
  const requestedScene = Number(params.get("scene") || "0");
  const [demoScene, setDemoScene] = useState(
    Number.isFinite(requestedScene) ? Math.max(0, Math.min(DEMO_SCENES.length - 1, requestedScene)) : 0
  );
  const [sitePasswordRequired, setSitePasswordRequired] = useState<boolean | null>(isDemo ? false : null);
  const [siteUnlocked, setSiteUnlocked] = useState(isDemo);
  const [sitePassword, setSitePassword] = useState(() => localStorage.getItem(SITE_KEY) || "");
  const [session, setSession] = useState<DisplaySession | null>(() => readDisplaySession());
  const [game, setGame] = useState<GameState | null>(isDemo ? DEMO_SCENES[demoScene] : null);
  const [status, setStatus] = useState<DisplayStatus>(isDemo ? "live" : "connecting");
  const [error, setError] = useState("");
  const reconnectTimer = useRef<number | null>(null);
  const socket = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (isDemo) return;
    fetch(apiUrl("/api/config"))
      .then((response) => response.json())
      .then(({ sitePasswordRequired: required }) => {
        setSitePasswordRequired(Boolean(required));
        if (!required) setSiteUnlocked(true);
        else if (sitePassword) setSiteUnlocked(true);
      })
      .catch(() => {
        setSitePasswordRequired(false);
        setSiteUnlocked(true);
      });
  }, [isDemo, sitePassword]);

  const connectDisplay = useCallback(async (activeSession: DisplaySession) => {
    setStatus("connecting");
    try {
      const response = await fetch(apiUrl("/api/connect"), {
        method: "POST",
        headers: sitePassword ? { Authorization: `Bearer ${sitePassword}` } : {}
      });
      const result = (await response.json()) as { ok: boolean; ticket?: string; error?: string };
      if (!response.ok || !result.ok || !result.ticket) {
        throw new Error(result.error || "The chamber refused entry.");
      }

      const endpoint = new URL(
        apiUrl(`/api/rooms/${encodeURIComponent(activeSession.code)}/socket`),
        window.location.href
      );
      endpoint.protocol = endpoint.protocol === "https:" ? "wss:" : "ws:";
      socket.current?.close(1000, "Reconnecting display");
      const nextSocket = new WebSocket(endpoint, [
        "veiled-court",
        `site.${result.ticket}`,
        `display.${activeSession.displayToken}`
      ]);
      socket.current = nextSocket;
      nextSocket.addEventListener("open", () => {
        setStatus("live");
        setError("");
      });
      nextSocket.addEventListener("message", (event) => {
        if (typeof event.data !== "string") return;
        try {
          const message = JSON.parse(event.data) as { type?: string; payload?: GameState };
          if (message.type === "room:state" && message.payload) setGame(message.payload);
        } catch {
          // Ignore malformed display messages.
        }
      });
      nextSocket.addEventListener("close", (event) => {
        if (socket.current !== nextSocket) return;
        setStatus("offline");
        if (event.code === 1000) return;
        reconnectTimer.current = window.setTimeout(() => {
          void connectDisplay(activeSession);
        }, 1200);
      });
    } catch (reason) {
      setStatus("offline");
      setError(reason instanceof Error ? reason.message : "The display could not connect.");
    }
  }, [sitePassword]);

  useEffect(() => {
    if (isDemo || !siteUnlocked || !session) return;
    void connectDisplay(session);
    return () => {
      if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
      socket.current?.close(1000, "Display closed");
      socket.current = null;
    };
  }, [connectDisplay, isDemo, session, siteUnlocked]);

  useEffect(() => {
    if (!isDemo) return;
    setGame(DEMO_SCENES[demoScene]);
  }, [demoScene, isDemo]);

  const leave = () => {
    socket.current?.close(1000, "Display left room");
    socket.current = null;
    localStorage.removeItem(DISPLAY_SESSION_KEY);
    setSession(null);
    setGame(null);
    setStatus("offline");
  };

  if (sitePasswordRequired === null) return <DisplayLoading />;
  if (sitePasswordRequired && !siteUnlocked) {
    return (
      <DisplayGate
        error={error}
        onUnlock={(password) => {
          setError("");
          setSitePassword(password);
          localStorage.setItem(SITE_KEY, password);
          setSiteUnlocked(true);
        }}
      />
    );
  }
  if (!session && !isDemo) {
    return (
      <DisplayJoin
        error={error}
        onJoin={async (code, password) => {
          setError("");
          const response = await fetch(apiUrl(`/api/rooms/${encodeURIComponent(code)}/watch`), {
            method: "POST",
            headers: {
              ...(sitePassword ? { Authorization: `Bearer ${sitePassword}` } : {}),
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ password })
          });
          const result = (await response.json()) as {
            ok: boolean;
            code?: string;
            displayToken?: string;
            error?: string;
          };
          if (!response.ok || !result.ok || !result.code || !result.displayToken) {
            setError(result.error || "The table could not find that chamber.");
            return;
          }
          const next = { code: result.code, displayToken: result.displayToken };
          localStorage.setItem(DISPLAY_SESSION_KEY, JSON.stringify(next));
          setSession(next);
        }}
      />
    );
  }
  if (!game) return <DisplayLoading label={status === "offline" ? "Trying the chamber again" : "Taking our place"} />;

  return (
    <TVTable
      game={game}
      status={status}
      isDemo={isDemo}
      demoScene={demoScene}
      onNextDemo={() => setDemoScene((current) => (current + 1) % DEMO_SCENES.length)}
      onLeave={leave}
    />
  );
}

function TVTable({
  game,
  status,
  isDemo,
  demoScene,
  onNextDemo,
  onLeave
}: {
  game: GameState;
  status: DisplayStatus;
  isDemo: boolean;
  demoScene: number;
  onNextDemo: () => void;
  onLeave: () => void;
}) {
  const announcement = describeMoment(game);
  const latest = game.log[game.log.length - 1];
  const active = game.players.find((player) => player.id === game.activePlayerId);
  const positions = seatPositions(game.players.length);

  return (
    <main
      className={`tv-stage tv-stage--${game.phase}`}
      onClick={isDemo ? onNextDemo : undefined}
      role={isDemo ? "button" : undefined}
      tabIndex={isDemo ? 0 : undefined}
      onKeyDown={(event) => {
        if (isDemo && (event.key === " " || event.key === "ArrowRight")) onNextDemo();
      }}
    >
      <div className="tv-stage__grain" />
      <div className="tv-stage__curtain tv-stage__curtain--left" />
      <div className="tv-stage__curtain tv-stage__curtain--right" />

      <header className="tv-masthead">
        <div className="tv-wordmark" aria-label="VEIL">
          <span>V</span>
          <b>VEIL</b>
          <small>THE COURT IS IN SESSION</small>
        </div>
        <div className="tv-room">
          <span>{isDemo ? "DEMO TABLE" : "ROOM"}</span>
          <b>{game.code}</b>
        </div>
        <div className={`tv-live tv-live--${status}`}>
          {status === "live" ? <Wifi size={18} /> : <WifiOff size={18} />}
          <span>{status === "live" ? "COURT LIVE" : status === "connecting" ? "REJOINING" : "OFFLINE"}</span>
        </div>
      </header>

      <section className="tv-room-floor" aria-label="Shared table state">
        <div className="tv-pool-of-light" />
        <div className="tv-table">
          <div className="tv-table__rim" />
          <div className="tv-table__felt">
            <div className="tv-table__filigree" />
            <AnimatePresence mode="wait">
              <motion.div
                className="tv-proclamation"
                key={`${game.phase}-${game.action?.id || game.turn}`}
                initial={{ opacity: 0, scale: 0.9, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.04, y: -12 }}
                transition={{ duration: 0.42 }}
              >
                <span className="tv-proclamation__turn">
                  {game.phase === "lobby" ? "THE GUEST LIST" : `TURN ${String(game.turn).padStart(2, "0")}`}
                </span>
                <span className="tv-proclamation__seal">{announcement.sigil}</span>
                <p>{announcement.eyebrow}</p>
                <h1>{announcement.title}</h1>
                <div className="tv-proclamation__rule" />
                <h2>{announcement.detail}</h2>
                {announcement.waiting && <small>{announcement.waiting}</small>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="tv-seats">
          {game.players.map((player, index) => (
            <TVSeat
              key={player.id}
              player={player}
              index={index}
              position={positions[index]}
              active={player.id === game.activePlayerId}
              involved={
                player.id === game.action?.actorId ||
                player.id === game.action?.targetId ||
                player.id === game.action?.blockerId
              }
            />
          ))}
        </div>
      </section>

      <footer className={`tv-ledger tv-ledger--${latest?.tone || "quiet"}`}>
        <span className="tv-ledger__label">
          <Radio size={15} />
          HEARD AT THE TABLE
        </span>
        <AnimatePresence mode="wait">
          <motion.p
            key={latest?.id}
            initial={{ opacity: 0, y: 9 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -9 }}
          >
            {latest?.text || "The chamber waits."}
          </motion.p>
        </AnimatePresence>
        <span className="tv-ledger__privacy">
          <EyeOff size={15} />
          PRIVATE INFLUENCE STAYS ON PHONES
        </span>
      </footer>

      {isDemo ? (
        <button className="tv-demo-control" type="button" onClick={(event) => {
          event.stopPropagation();
          onNextDemo();
        }}>
          MOMENT {demoScene + 1}/{DEMO_SCENES.length} · NEXT
        </button>
      ) : (
        <button className="tv-leave" type="button" onClick={onLeave} aria-label="Leave table display">
          <LogOut size={18} />
        </button>
      )}
    </main>
  );
}

function TVSeat({
  player,
  index,
  position,
  active,
  involved
}: {
  player: Player;
  index: number;
  position: { x: number; y: number };
  active: boolean;
  involved: boolean;
}) {
  return (
    <motion.article
      className={[
        "tv-seat",
        active ? "tv-seat--active" : "",
        involved ? "tv-seat--involved" : "",
        !player.alive ? "tv-seat--out" : "",
        !player.connected ? "tv-seat--offline" : ""
      ].filter(Boolean).join(" ")}
      style={{
        "--seat-x": `${position.x}%`,
        "--seat-y": `${position.y}%`,
        "--seat-delay": `${index * 70}ms`
      } as React.CSSProperties}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="tv-seat__spotlight" />
      {/* A face card is public room state, so the shared screen shows it too —
          the medallion frame is kept either way. */}
      <div className="tv-seat__medallion">
        {player.avatar ? (
          <img src={player.avatar} alt="" />
        ) : (
          player.name.slice(0, 1).toUpperCase()
        )}
      </div>
      <div className="tv-seat__plaque">
        <div className="tv-seat__identity">
          <span>{active ? "HAS THE FLOOR" : player.bot ? "CLOCKWORK GUEST" : `SEAT ${index + 1}`}</span>
          <b>{player.name}</b>
        </div>
        <div className="tv-seat__influence" aria-label={`${player.influence.filter((card) => !card.revealed).length} hidden Influence`}>
          {player.influence.map((card) =>
            card.revealed && card.role ? (
              <span
                className="tv-influence tv-influence--revealed"
                key={card.id}
                style={{ "--role-color": ROLES[card.role].color } as React.CSSProperties}
                title={`Revealed ${ROLES[card.role].name}`}
              >
                {ROLES[card.role].sigil}
              </span>
            ) : (
              <span className="tv-influence" key={card.id} title="Hidden Influence">
                V
              </span>
            )
          )}
        </div>
        <div className="tv-seat__coins" aria-label={`${player.coins} Coins`}>
          <span className="tv-coin-stack" />
          <b>{player.coins}</b>
        </div>
      </div>
      {!player.connected && !player.bot && <span className="tv-seat__away">RECONNECTING</span>}
    </motion.article>
  );
}

function DisplayJoin({
  error,
  onJoin
}: {
  error: string;
  onJoin: (code: string, password: string) => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    await onJoin(code.trim().toUpperCase(), password);
    setBusy(false);
  };
  return (
    <main className="tv-entry">
      <div className="tv-stage__grain" />
      <section className="tv-entry__card">
        <div className="tv-entry__seal">V</div>
        <span>VEIL · TABLE DISPLAY</span>
        <h1>Bring the court<br />into the room.</h1>
        <p>Players keep their phones. This screen carries only what everyone at the table may know.</p>
        <form onSubmit={submit}>
          <label>
            <span>Room code</span>
            <input
              className="tv-entry__code"
              value={code}
              onChange={(event) => setCode(event.target.value.slice(0, 4).toUpperCase())}
              placeholder="ABCD"
              autoCapitalize="characters"
              required
            />
          </label>
          <label>
            <span>Room passphrase</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Shared with the room"
              required
            />
          </label>
          <button type="submit" disabled={busy || code.length !== 4}>
            {busy ? "TAKING OUR PLACE…" : "JOIN AS THE TABLE"}
          </button>
        </form>
        {error && <small className="tv-entry__error">{error}</small>}
        <small><EyeOff size={14} /> Read-only · no player seat · no private cards</small>
      </section>
    </main>
  );
}

function DisplayGate({
  error,
  onUnlock
}: {
  error: string;
  onUnlock: (password: string) => void;
}) {
  const [password, setPassword] = useState("");
  return (
    <main className="tv-entry">
      <div className="tv-stage__grain" />
      <section className="tv-entry__card">
        <div className="tv-entry__seal">V</div>
        <span>PRIVATE PERFORMANCE</span>
        <h1>The doors are closed.</h1>
        <form onSubmit={(event) => {
          event.preventDefault();
          onUnlock(password);
        }}>
          <label>
            <span>Site access phrase</span>
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
          </label>
          <button type="submit">OPEN THE DOORS</button>
        </form>
        {error && <small className="tv-entry__error">{error}</small>}
      </section>
    </main>
  );
}

function DisplayLoading({ label = "Preparing the court" }: { label?: string }) {
  return (
    <main className="tv-entry">
      <div className="tv-stage__grain" />
      <div className="tv-loading">
        <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 6, ease: "linear" }}>V</motion.span>
        <b>{label}</b>
      </div>
    </main>
  );
}

function describeMoment(game: GameState) {
  const playerName = (id: string | null | undefined) =>
    game.players.find((player) => player.id === id)?.name;
  const actor = playerName(game.action?.actorId);
  const target = playerName(game.action?.targetId);
  const actionName = game.action ? ACTIONS[game.action.type].name : "";
  const waitingIds = game.window?.eligibleIds.filter((id) => !game.window?.passes.includes(id)) || [];
  const waiting = waitingIds.length
    ? `WAITING ON ${waitingIds.map((id) => playerName(id)).filter(Boolean).join(" · ")}`
    : "";

  if (game.phase === "lobby") {
    return {
      sigil: "V",
      eyebrow: `${game.players.length} ${game.players.length === 1 ? "GUEST" : "GUESTS"} SEATED`,
      title: "The court is gathering.",
      detail: "Join on your phones. The host will seal the chamber.",
      waiting: ""
    };
  }
  if (game.phase === "game_over") {
    return {
      sigil: "♛",
      eyebrow: "THE FINAL SECRET",
      title: `${playerName(game.winnerId)} keeps the court.`,
      detail: "The veil has fallen.",
      waiting: ""
    };
  }
  if (game.phase === "turn") {
    return {
      sigil: String(game.turn).padStart(2, "0"),
      eyebrow: "THE FLOOR IS OPEN",
      title: `${playerName(game.activePlayerId)} must act.`,
      detail: "Income, bluff, or strike. The room is watching.",
      waiting: ""
    };
  }
  if (game.phase === "response_window") {
    // One window now offers pass, doubt and block together, and a block declared
    // inside it is public the instant it lands — even while the doubt against the
    // actor is still settling. The shared screen says whichever is further along.
    const blocker = playerName(game.window?.blockerId);
    const blockRole = game.window?.blockRole ? ROLES[game.window.blockRole].name : "";
    if (blocker) {
      return {
        sigil: "✋",
        eyebrow: `${blocker} BLOCKS AS ${blockRole.toUpperCase()}`,
        title: `${actionName} is held at the door.`,
        detail: "The doubt against the claim settles first.",
        waiting
      };
    }
    const claimRole = game.window?.claimRole ? ROLES[game.window.claimRole].name : "";
    return {
      sigil: "✋",
      eyebrow: `${actor} DECLARES ${actionName.toUpperCase()}`,
      title: target ? `${target} is in the line of fire.` : "Will anyone stand in the way?",
      detail: claimRole
        ? `Doubt the ${claimRole}, block it, or let it pass on your phones.`
        : "Block it or let it pass on your phones.",
      waiting
    };
  }
  if (game.phase === "claim_window") {
    // A claim window is only ever the doubt against a BLOCK, so the claimant
    // here is the blocker and the claim is the role they blocked with.
    const claimant = playerName(game.window?.claimantId) || actor;
    const role = game.window?.claimRole ? ROLES[game.window.claimRole].name : "role";
    return {
      sigil: "!",
      eyebrow: `${claimant} BLOCKS AS ${role.toUpperCase()}`,
      title: "Truth—or an excellent lie?",
      detail: "Doubt the block or let it stand on your phones.",
      waiting
    };
  }
  if (game.phase === "lose_influence") {
    // Either outcome of a settled doubt arrives here already masked to
    // "callout" — the shared screen is handed sanitizeRoom(room, null) and is
    // therefore never the seat that owes the card. The payload is identical for
    // a caught bluff and a proven claim, playerId stripped from both, and this
    // branch must stay that way: naming who is choosing would hand the room the
    // verdict, because the loser IS the verdict. Say who doubted whom and what
    // was claimed, and let the card turning over do the rest.
    if (game.pendingLoss?.kind === "callout") {
      const claimed = game.pendingLoss.claimRole ? ROLES[game.pendingLoss.claimRole].name : "";
      const challenger = playerName(game.pendingLoss.challengerId);
      const claimant = playerName(game.pendingLoss.claimantId);
      return {
        sigil: "?",
        eyebrow: claimed ? `THE ${claimed.toUpperCase()} IS DOUBTED` : "A CLAIM IS DOUBTED",
        title:
          challenger && claimant
            ? `${challenger} doubts ${claimant}'s ${claimed || "claim"}.`
            : "A claim has been doubted.",
        detail: "Nobody knows the truth until a card turns over.",
        waiting: "AN INFLUENCE IS BEING CHOSEN"
      };
    }
    // Every remaining kind is public: playerId survives sanitising and the cause
    // is the same string for every viewer, so both can be printed verbatim.
    const losingPlayer = playerName(game.pendingLoss?.playerId) || "A player";
    return {
      sigil: "×",
      eyebrow: game.pendingLoss?.cause?.toUpperCase() || "A PRICE IS DUE",
      title: `${losingPlayer} must surrender Influence.`,
      detail: "The choice remains private until it is revealed.",
      waiting: `WAITING ON ${losingPlayer}`
    };
  }
  if (game.phase === "exchange") {
    const envoy = playerName(game.exchangeChoices?.playerId) || actor;
    return {
      sigil: "↻",
      eyebrow: "THE AMBASSADOR ENTERS",
      title: `${envoy} changes the masks.`,
      detail: "The exchange stays entirely on their phone.",
      waiting: `WAITING ON ${envoy}`
    };
  }
  return {
    sigil: "V",
    eyebrow: "THE COURT WAITS",
    title: "A secret is moving.",
    detail: "Watch the table.",
    waiting: ""
  };
}

function seatPositions(count: number) {
  const layouts: Record<number, Array<{ x: number; y: number }>> = {
    1: [{ x: 50, y: 84 }],
    2: [{ x: 21, y: 73 }, { x: 79, y: 73 }],
    3: [{ x: 50, y: 17 }, { x: 17, y: 75 }, { x: 83, y: 75 }],
    4: [{ x: 26, y: 17 }, { x: 74, y: 17 }, { x: 17, y: 76 }, { x: 83, y: 76 }],
    5: [{ x: 50, y: 13 }, { x: 16, y: 36 }, { x: 22, y: 80 }, { x: 78, y: 80 }, { x: 84, y: 36 }],
    6: [{ x: 30, y: 13 }, { x: 70, y: 13 }, { x: 13, y: 52 }, { x: 25, y: 84 }, { x: 75, y: 84 }, { x: 87, y: 52 }]
  };
  return layouts[count] || layouts[6];
}

function readDisplaySession(): DisplaySession | null {
  try {
    const raw = localStorage.getItem(DISPLAY_SESSION_KEY);
    return raw ? JSON.parse(raw) as DisplaySession : null;
  } catch {
    return null;
  }
}

function makeDemoScenes(): GameState[] {
  const players: Player[] = [
    demoPlayer("ada", "Ada", 5, [false, false]),
    demoPlayer("bex", "Bex", 2, [true, false], "Knife"),
    demoPlayer("cy", "Cy", 7, [false, false]),
    demoPlayer("dee", "Dee", 3, [false, true], "Corsair"),
    demoPlayer("eli", "Eli", 1, [false, false]),
    demoPlayer("fox", "Fox", 6, [true, true], "Regent")
  ];
  const base: GameState = {
    code: "VEIL",
    hostId: "ada",
    phase: "turn",
    activePlayerId: "cy",
    turn: 8,
    action: null,
    window: null,
    pendingLoss: null,
    exchangeChoices: null,
    winnerId: null,
    players,
    lastPlay: null,
    log: [
      { id: "l1", text: "Ada takes Income.", tone: "gain" },
      { id: "l2", text: "Bex loses the Assassin.", tone: "loss" },
      { id: "l3", text: "Cy holds the next move.", tone: "turn" }
    ]
  };
  // Fox is out of the game (both cards revealed), so no scene ever lists Fox as
  // owing an answer. Timestamps are frozen so ?scene=N is reproducible.
  return [
    base,
    {
      ...base,
      phase: "response_window",
      action: {
        id: "action-steal",
        type: "seize",
        actorId: "cy",
        targetId: "ada",
        claimRole: "Corsair",
        blockerId: null,
        blockRole: null
      },
      // One window: every living opponent may doubt the Corsair claim, and the
      // target alone may block it.
      window: {
        kind: "response",
        stage: "pre_action",
        source: "action",
        claimantId: "cy",
        claimRole: "Corsair",
        claimProven: false,
        challengeIds: ["ada", "bex", "dee", "eli"],
        blockIds: ["ada"],
        eligibleIds: ["ada", "bex", "dee", "eli"],
        allowedRoles: ["Corsair", "Envoy"],
        passes: [],
        blockerId: null,
        blockRole: null,
        durationMs: 5000,
        msLeft: 3100
      },
      lastPlay: {
        id: "play-steal",
        at: DEMO_AT,
        playerId: "cy",
        actionType: "seize",
        targetId: "ada"
      },
      log: [...base.log, { id: "l4", text: "Cy declares Steal against Ada.", tone: "action" }]
    },
    {
      ...base,
      phase: "claim_window",
      // A claim window is only ever the doubt against a block, so this scene
      // shows Ada's Duke standing between Eli and the treasury.
      action: {
        id: "action-aid",
        type: "patronage",
        actorId: "eli",
        targetId: null,
        claimRole: null,
        blockerId: "ada",
        blockRole: "Regent"
      },
      window: {
        kind: "claim",
        stage: "post_block",
        source: "block",
        claimantId: "ada",
        claimRole: "Regent",
        claimProven: false,
        challengeIds: ["bex", "cy", "dee", "eli"],
        blockIds: [],
        eligibleIds: ["bex", "cy", "dee", "eli"],
        allowedRoles: [],
        passes: ["bex"],
        blockerId: null,
        blockRole: null,
        durationMs: 5000,
        msLeft: 3400
      },
      lastPlay: {
        id: "play-aid",
        at: DEMO_AT,
        playerId: "eli",
        actionType: "patronage",
        targetId: null
      },
      log: [
        ...base.log,
        { id: "l5", text: "Eli declares Foreign Aid.", tone: "action" },
        { id: "l5b", text: "Ada blocks as Duke.", tone: "block" }
      ]
    },
    {
      ...base,
      phase: "lose_influence",
      activePlayerId: "dee",
      action: {
        id: "action-coup",
        type: "banish",
        actorId: "dee",
        targetId: "cy",
        claimRole: null,
        blockerId: null,
        blockRole: null
      },
      // A Coup is nobody's doubt, so there is no challenger to credit the loss to
      // and no claim was ever made. Its kind is public: playerId is never masked
      // and the cause prints as-is.
      pendingLoss: {
        playerId: "cy",
        cause: "Coup",
        challengerId: null,
        claimantId: null,
        claimRole: null,
        kind: "coup",
        durationMs: 12000,
        msLeft: 7400
      },
      lastPlay: {
        id: "play-coup",
        at: DEMO_AT,
        playerId: "dee",
        actionType: "banish",
        targetId: "cy"
      },
      log: [...base.log, { id: "l6", text: "Dee declares Coup against Cy.", tone: "danger" }]
    },
    {
      ...base,
      phase: "exchange",
      activePlayerId: "ada",
      action: {
        id: "action-exchange",
        type: "exchange",
        actorId: "ada",
        targetId: null,
        claimRole: "Envoy",
        blockerId: null,
        blockRole: null
      },
      // The display is handed sanitizeRoom(room, null), which always empties
      // this list — the shared screen never sees the cards on offer.
      exchangeChoices: { playerId: "ada", cards: [] },
      lastPlay: {
        id: "play-exchange",
        at: DEMO_AT,
        playerId: "ada",
        actionType: "exchange",
        targetId: null
      },
      log: [...base.log, { id: "l7", text: "Ada proves Ambassador.", tone: "proof" }]
    },
    {
      ...base,
      phase: "lose_influence",
      activePlayerId: "eli",
      action: {
        id: "action-tax",
        type: "levy",
        actorId: "eli",
        targetId: null,
        claimRole: "Regent",
        blockerId: null,
        blockRole: null
      },
      // The masked shape, which is the only pendingLoss the shared screen can
      // ever be handed with a claim attached: Bex has doubted Eli's Duke and
      // somebody is choosing a card. Which of the two it is — Eli caught, or Bex
      // wrong — is exactly what playerId would give away, so it is null here
      // just as sanitizeRoom sends it. The log stops at the doubt for the same
      // reason: there is no "Eli proves Duke." line to ship yet.
      pendingLoss: {
        playerId: null,
        cause: "Called out",
        challengerId: "bex",
        claimantId: "eli",
        claimRole: "Regent",
        kind: "callout",
        durationMs: 12000,
        msLeft: 8200
      },
      lastPlay: {
        id: "play-tax",
        at: DEMO_AT,
        playerId: "eli",
        actionType: "levy",
        targetId: null
      },
      log: [
        ...base.log,
        { id: "l8", text: "Eli declares Tax.", tone: "action" },
        { id: "l8b", text: "Bex doubts Eli's Duke.", tone: "danger" }
      ]
    }
  ];
}

function demoPlayer(
  id: string,
  name: string,
  coins: number,
  revealed: boolean[],
  revealedRole: keyof typeof ROLES = "Regent"
): Player {
  return {
    id,
    name,
    // The demo scenes fall back to initials rather than ship face-card data URLs.
    avatar: null,
    coins,
    connected: true,
    alive: revealed.some((value) => !value),
    // Recalled by holding a seat on a phone; the shared screen has no hold.
    recentPlays: [],
    influence: revealed.map((isRevealed, index) => ({
      id: `${id}-card-${index}`,
      role: isRevealed ? revealedRole : null,
      revealed: isRevealed
    }))
  };
}
